import { createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '../Axios';
import { toggleProjectPlanModal } from 'reducers/UiReducer';

const getAxiosConfig = (token) => ({
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  },
});

const getErrorMessage = (error) =>
  error.response && error.response.data.message ? error.response.data.message : error.message;

const ProjectPlanReducer = createSlice({
  name: 'projectPlan',
  initialState: {
    projectPlanLoading: false,
    projectPlanList: [],
    projectPlanError: null,
    projectPlanSuccessMessage: '',
  },
  reducers: {
    createProjectPlanRequest(state) {
      state.projectPlanLoading = true;
      state.projectPlanError = null;
      state.projectPlanSuccessMessage = '';
    },
    createProjectPlanSuccess(state, action) {
      state.projectPlanLoading = false;
      state.projectPlanSuccessMessage = action.payload || 'Project plan saved successfully';
    },
    createProjectPlanFail(state, action) {
      state.projectPlanLoading = false;
      state.projectPlanError = action.payload;
    },
    updateProjectPlanRequest(state) {
      state.projectPlanLoading = true;
      state.projectPlanError = null;
      state.projectPlanSuccessMessage = '';
    },
    updateProjectPlanSuccess(state, action) {
      state.projectPlanLoading = false;
      state.projectPlanSuccessMessage = action.payload || 'Project plan updated successfully';
    },
    updateProjectPlanFail(state, action) {
      state.projectPlanLoading = false;
      state.projectPlanError = action.payload;
    },
    getProjectPlanListRequest(state) {
      state.projectPlanLoading = true;
    },
    getProjectPlanListSuccess(state, action) {
      state.projectPlanLoading = false;
      state.projectPlanList = action.payload;
    },
    getProjectPlanListFail(state, action) {
      state.projectPlanLoading = false;
      state.projectPlanError = action.payload;
    },
    getProjectPlanByIdRequest(state) {
      state.projectPlanLoading = true;
      state.projectPlanError = null;
    },
    getProjectPlanByIdSuccess(state) {
      state.projectPlanLoading = false;
    },
    getProjectPlanByIdFail(state, action) {
      state.projectPlanLoading = false;
      state.projectPlanError = action.payload;
    },
  },
});

export const {
  createProjectPlanRequest,
  createProjectPlanSuccess,
  createProjectPlanFail,
  updateProjectPlanRequest,
  updateProjectPlanSuccess,
  updateProjectPlanFail,
  getProjectPlanListRequest,
  getProjectPlanListSuccess,
  getProjectPlanListFail,
  getProjectPlanByIdRequest,
  getProjectPlanByIdSuccess,
  getProjectPlanByIdFail,
} = ProjectPlanReducer.actions;

export const createProjectPlanDispatch = (projectPlanData, token, listRequest) => async (dispatch) => {
  try {
    dispatch(createProjectPlanRequest());
    const { data } = await axiosInstance.post(
      '/ProjectPlan/CreateProjectPlan',
      projectPlanData,
      getAxiosConfig(token)
    );

    if (data.status === 1) {
      dispatch(createProjectPlanSuccess(data.message || 'Project plan saved successfully'));
      dispatch(
        getProjectPlanListDispatch(
          listRequest || { ProjectPlanId: 0, PlanTitle: '', Offset: 0, RecordCount: 20 },
          token
        )
      );
      dispatch(toggleProjectPlanModal(false));
    } else {
      dispatch(createProjectPlanFail(data.message || 'Unable to save project plan'));
    }

    return data;
  } catch (error) {
    dispatch(createProjectPlanFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const updateProjectPlanDispatch = (projectPlanData, token, listRequest) => async (dispatch) => {
  try {
    dispatch(updateProjectPlanRequest());
    const { data } = await axiosInstance.post(
      '/ProjectPlan/UpdateProjectPlan',
      projectPlanData,
      {
        ...getAxiosConfig(token),
        params: {
          ProjectPlanId: projectPlanData.projectPlanId,
        },
      }
    );

    if (data.status === 1) {
      dispatch(updateProjectPlanSuccess(data.message || 'Project plan updated successfully'));
      dispatch(
        getProjectPlanListDispatch(
          listRequest || { ProjectPlanId: 0, PlanTitle: '', Offset: 0, RecordCount: 20 },
          token
        )
      );
      dispatch(toggleProjectPlanModal(false));
    } else {
      dispatch(updateProjectPlanFail(data.message || 'Unable to update project plan'));
    }

    return data;
  } catch (error) {
    dispatch(updateProjectPlanFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const getProjectPlanListDispatch = (form, token) => async (dispatch) => {
  try {
    dispatch(getProjectPlanListRequest());

    const projectPlanId =
      form?.ProjectPlanId ?? form?.projectPlanId ?? form?.projectPlanDto?.projectPlanId ?? 0;
    const projectId = form?.ProjectId ?? form?.projectId ?? form?.projectPlanDto?.projectId ?? 0;
    const planTitle = form?.PlanTitle ?? form?.planTitle ?? form?.projectPlanDto?.planTitle ?? '';
    const offset = form?.Offset ?? form?.offset ?? form?.projectPlanDto?.offset ?? 0;
    const recordCount =
      form?.RecordCount ?? form?.recordCount ?? form?.projectPlanDto?.recordCount ?? 20;

    const { data } = await axiosInstance.get('/ProjectPlan/GetProjectPlanList', {
      ...getAxiosConfig(token),
      params: {
        ProjectPlanId: projectPlanId || 0,
        ProjectId: projectId || 0,
        PlanTitle: planTitle,
        Offset: offset,
        RecordCount: recordCount,
      },
    });

    dispatch(getProjectPlanListSuccess(data));
  } catch (error) {
    dispatch(getProjectPlanListFail(getErrorMessage(error)));
  }
};

export const getProjectPlanByIdDispatch = (projectPlanId, token) => async (dispatch) => {
  try {
    dispatch(getProjectPlanByIdRequest());
    const { data } = await axiosInstance.get('/ProjectPlan/GetProjectPlanById', {
      ...getAxiosConfig(token),
      params: {
        ProjectPlanId: projectPlanId,
      },
    });

    dispatch(getProjectPlanByIdSuccess());
    return data;
  } catch (error) {
    dispatch(getProjectPlanByIdFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export default ProjectPlanReducer;
